import { useCallback, useEffect, useRef, useState } from 'react';
import { getDevices, type DeviceSortBy, type SortDir } from '@/features/devices/api/devices-api';
import type { Device } from '@/types/api';

// Visibility-aware polling: tight cadence while the tab is focused so the
// phone-paired and PC views stay close to live, backs off when hidden so
// background tabs don't hammer the API.
const DEFAULT_POLL_VISIBLE_MS = 3_000;
const DEFAULT_POLL_HIDDEN_MS = 15_000;

interface UseSetupModePollingParams {
  enabled: boolean;
  propertyId: string | undefined;
  buildingId: string | undefined;
  perPage?: number;
  /** Sort field. Defaults to 'unit' (matches property device list). */
  sortBy?: DeviceSortBy;
  /** Sort direction. Defaults to 'asc'. */
  sortDir?: SortDir;
  /** Override the visible-tab interval. Defaults to 3s. */
  visibleIntervalMs?: number;
  /** Override the hidden-tab interval. Defaults to 15s. */
  hiddenIntervalMs?: number;
}

interface UseSetupModePollingResult {
  devices: Device[];
  refetch: () => Promise<void>;
  loading: boolean;
  // True once the first fetch in the current session has returned. Used by
  // the table so it can fall back to the portal's devices list during the
  // moment between mode-enter and the first poll response (prevents a
  // visible blank-table flash).
  hasLoaded: boolean;
}

// While Setup Mode is active, poll the property's devices so rows flip
// from red → green as magnet transmissions arrive. Stops polling the moment
// the mode exits or the component unmounts.
export function useSetupModePolling({
  enabled,
  propertyId,
  buildingId,
  perPage = 100,
  sortBy = 'unit',
  sortDir = 'asc',
  visibleIntervalMs = DEFAULT_POLL_VISIBLE_MS,
  hiddenIntervalMs = DEFAULT_POLL_HIDDEN_MS,
}: UseSetupModePollingParams): UseSetupModePollingResult {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const cancelledRef = useRef(false);

  const fetchDevices = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      // Server-side sort via `UnitNumberComparer` for unit / by CreatedAt
      // otherwise. Defaults match the property device list (unit asc) so
      // toggling Setup Mode doesn't reshuffle the grid.
      const res = await getDevices({
        propertyId,
        buildingId: buildingId || undefined,
        perPage,
        sortBy,
        sortDir,
      });
      if (!cancelledRef.current) {
        setDevices(res.items);
        setHasLoaded(true);
      }
    } catch {
      // Transient poll failures are non-fatal; the next tick will retry.
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [propertyId, buildingId, perPage, sortBy, sortDir]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled || !propertyId) {
      setDevices([]);
      setHasLoaded(false);
      return;
    }

    let intervalId: number | undefined;
    const schedule = () => {
      if (intervalId !== undefined) window.clearInterval(intervalId);
      const delay = document.visibilityState === 'visible' ? visibleIntervalMs : hiddenIntervalMs;
      intervalId = window.setInterval(() => { void fetchDevices(); }, delay);
    };

    void fetchDevices();
    schedule();

    const onVisibilityChange = () => {
      // Re-fetch immediately on becoming visible so the user sees fresh state
      // without waiting a full poll cycle after tab-switching back.
      if (document.visibilityState === 'visible') void fetchDevices();
      schedule();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelledRef.current = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, propertyId, buildingId, visibleIntervalMs, hiddenIntervalMs, fetchDevices]);

  return { devices, refetch: fetchDevices, loading, hasLoaded };
}
